import { useEffect, useMemo, useState } from "react";
import { base64ToUint8Array } from "utilities/file";
import { useNavigate } from "react-router";
import { useSelector, useDispatch } from "react-redux";
import upload from "assets/images/v2/upload-icon.svg";
import styles from "./Documents.module.scss";
import DocumentCard, { DocumentDateLabel } from "components/v2/document-card/DocumentCard";
import docErrorImg from "assets/images/v2/error-illustration.svg";
import noDocumentImg from "assets/images/v2/no-documents.svg";
import { useGetFeatureFlagsQuery } from "services/common/featureFlagService";
import {
  dashboardApi,
  useRetrieveNeedsQuery,
  RetrieveNeedDto,
  RetrieveDocumentsResponseDto,
  DocumentStatus,
  useRetrievePalContentQuery,
  useRetrieveDocumentsForDealApplicationQuery,
  RetrievePalResponseDto,
  useViewedDocumentsLogMutation,
} from "pages/dashboard/home/dashboardService";
import {
  getCurrentUserId,
  getDealApplicationId,
  getLoanId,
  getNhDealApplicationId,
  setLogout,
  getCurrentUserName,
  getProspectId,
  getLoanNumber,
  getDealId,
  getEncompassLoanId,
  getApplicationRoleType,
} from "features/authSlice";
import { Dropdown, DropdownOption } from "components/v2/dropdown/Dropdown";
import { SORT_BY_DATE, SORT_BY_TITLE, FILTER_NAF, ApplicationRoleTypes } from "utilities/Constant";
import TogglePills from "components/v2/toggle-pills/TogglePills";
import useDesktopChecker from "hooks/useDesktopChecker";
import SearchBar from "components/v2/search-bar/SearchBar";
import { LoadingPageProps } from "../loadingPageProps";
import Drawer, { DrawerType } from "components/ui/drawer/Drawer";
import { Spinner } from "components/v2/spinner/spinner";
import FileUploadPanel from "components/v2/file-upload-panel/FileUploadPanel";
import DocumentPreview from "components/v2/document-preview/DocumentPreview";
import searchNotFoundImg from "assets/images/v2/search-not-found.svg";
import { trackFSDocumentsUploaded, trackFSGuidedExperienceFirstTaskCompletedIfEligible } from "utilities/full-story";
import { getLoanProgress } from "pages/dashboard/store/dashboardSlice";

function previewFileName(document: RetrieveDocumentsResponseDto, contentName?: string): string {
  if (!document.isWireFraudDocument) {
    return document.name;
  }

  const name = contentName?.trim() || document.name;
  return name.toLowerCase().endsWith(".pdf") ? name : `${name}.pdf`;
}

function getDocumentDateLabel(doc: RetrieveDocumentsResponseDto): DocumentDateLabel {
  if (!doc.isWireFraudDocument) {
    return "uploaded";
  }

  return doc.acknowledgedOn ? "viewed" : "none";
}

function getDocumentSortTime(doc: RetrieveDocumentsResponseDto): number {
  const value = doc.isWireFraudDocument ? doc.acknowledgedOn : doc.dateCreated;
  if (!value) {
    return 0;
  }

  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
}

const Documents: React.FC<LoadingPageProps & { pendingDocumentId?: string | null; onDocumentOpened?: () => void }> = ({
  onLoad,
  pendingDocumentId,
  onDocumentOpened,
}: LoadingPageProps & { pendingDocumentId?: string | null; onDocumentOpened?: () => void }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [fileInfo, setFileInfo] = useState({ filePath: "", fileName: "", documentId: "" });
  const [needsByUser, setNeedsByUser] = useState<RetrieveNeedDto[]>([]);
  const { data: featureFlag } = useGetFeatureFlagsQuery();
  const [invokeDocumentContent, { isLoading: isDocumentLoading, isFetching: isDocumentFetching, isError: isDocumentError }] =
    dashboardApi.endpoints.retrieveDocumentContent.useLazyQuery();
  const nhDealApplicationId = useSelector(getNhDealApplicationId);
  const dealApplicationId = useSelector(getDealApplicationId);
  const loanId = useSelector(getLoanId);
  const [documentsByNaf, setDocumentsByNaf] = useState<RetrievePalResponseDto[]>([]);
  const isDesktop = useDesktopChecker();
  const [fileName, setFileName] = useState("");
  const [selectedDocument, setSelectedDocument] = useState<RetrieveDocumentsResponseDto | null>(null);
  const [showDrawer, setShowDrawer] = useState(false);
  const [updateViewedDocuments] = useViewedDocumentsLogMutation();
  const [shouldRefetchDocList, setShouldRefetchDocList] = useState(false);
  const userId = useSelector(getCurrentUserId);
  const username = useSelector(getCurrentUserName);
  const prospectId = useSelector(getProspectId);
  const dealId = useSelector(getDealId);
  const loanNumber = useSelector(getLoanNumber);
  const loanProgress = useSelector(getLoanProgress);
  const losId = useSelector(getEncompassLoanId);
  const applicationRoleType = useSelector(getApplicationRoleType);
  const isNonBorrowerOwner = applicationRoleType.toLowerCase() === ApplicationRoleTypes.NonBorrowerOwner.toLowerCase();


  const {
    data: retrievePalData,
    isLoading: isRetreivePalLoading,
    isSuccess: isRetreivePalSuccess,
    isError: isRetrievePalError,
  } = useRetrievePalContentQuery(
    { nhDealApplicationId, loanId },
    {
      skip: !featureFlag?.preApprovalLetterEnabled || loanId === "",
    },
  );

  const documentFilters = [
    { label: isDesktop ? "All Documents" : "All", value: "all" },
    { label: "NAF Documents", value: FILTER_NAF },
    { label: "Your Documents", value: "user" },
  ] as const;
  type FilterType = typeof documentFilters[number]["value"]; // 'all' | 'naf' | 'user'

  const [filter, setFilter] = useState<FilterType>("all");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [sortField, setSortField] = useState<string>(SORT_BY_DATE);
  const [disableSort, setDisableSort] = useState<boolean>(true);
  const [disableUploadBtn, setDisableUploadBtn] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [documentLoadError, setDocumentLoadError] = useState(false);
  const {
    data: retrieveNeedsData,
    isLoading: isRetrieveNeedsLoading,
    isFetching: isDocListFetching,
    isSuccess: isRetrieveNeedsSuccess,
    isError: isRetrieveNeedsError,
    refetch: refetchDocList,
  } = useRetrieveNeedsQuery(
    { nhDealApplicationId, userId },
    {
      skip: !nhDealApplicationId,
    },
  );
  const { data: dealApplicationDocuments } = useRetrieveDocumentsForDealApplicationQuery(
    { dealApplicationId, userId },
    { skip: !dealApplicationId },
  );

  useEffect(() => {
    if (!isRetrieveNeedsLoading && isRetrieveNeedsSuccess && retrieveNeedsData) {
      setNeedsByUser(retrieveNeedsData);
    }
  }, [isRetrieveNeedsLoading, isRetrieveNeedsSuccess, retrieveNeedsData]);

  useEffect(() => {
    if (loanId && !isRetreivePalLoading && isRetreivePalSuccess && retrievePalData !== documentsByNaf) {
      setDocumentsByNaf(retrievePalData);
    }
  }, [isRetreivePalLoading, isRetreivePalSuccess, loanId]);

  const transformNeeds = (needs: RetrieveNeedDto[]): RetrieveDocumentsResponseDto[] =>
    needs
      .flatMap((need) => need.documents)
      .map((doc) => ({
        fileId: doc.id,
        name: doc.docName ?? "",
        documentPath: "",
        dateCreated: doc.uploadedDateTime ?? "",
        category: "",
        userName: `${doc.uploader.firstName} ${doc.uploader.lastName}`,
        status: doc.status,
        invalidReason: doc.invalidReason ?? "",
        detailedExplanation: doc.detailedExplanation ?? "",
        userNotified: doc.userNotified,
        isNoPreview: doc.isNoPreview ?? false,
      }));

  const transformDocuments = (documents: RetrievePalResponseDto[]): RetrieveDocumentsResponseDto[] => {
    return (documents || []).map((doc: RetrievePalResponseDto) => ({
      fileId: doc.palInstanceId,
      name: doc.documentContent?.name ?? "",
      documentPath: doc.documentContent?.fileContents ?? "",
      dateCreated: doc.createdDate ?? "",
      category: "",
      isNoPreview: false,
    }));
  };

  const isDocumentMarkedInvalid = (doc: RetrieveDocumentsResponseDto): boolean =>
    doc.status?.toLowerCase() === DocumentStatus.Rejected.toLowerCase() && !!doc.invalidReason && !!doc.detailedExplanation && !!doc.userNotified;

  const getBlob = async (document: RetrieveDocumentsResponseDto): Promise<{ url: string; fileName: string } | null> => {
    try {
      const TIMEOUT_MS = 30000;
      const timeoutPromise = new Promise<never>((_, reject) => setTimeout(() => reject(new Error("Request timed out")), TIMEOUT_MS));
      const response = await Promise.race([invokeDocumentContent(document.fileId), timeoutPromise]);
      const { data, isError, error } = response;

      if (isError && error && typeof error === "object" && "status" in error && (error as { status?: number }).status === 401) {
        dispatch(setLogout());
        navigate("/", { replace: true });
        return null;
      }

      if (data) {
        const blob = new Blob([base64ToUint8Array(data.fileContents)], { type: data.contentType });
        const url = URL.createObjectURL(blob);
        return { url, fileName: previewFileName(document, data.name) };
      }
    } catch (error) {
      console.error("Error getting document blob:", error);
      return null;
    }

    return null;
  };

  const getPathUrl = (document: RetrieveDocumentsResponseDto, contentType?: string) => {
    if (document) {
      const blob = new Blob([base64ToUint8Array(document.documentPath)], { type: contentType });
      const url = globalThis.URL.createObjectURL(blob);
      void updateViewedDocuments({ userId, documentIds: [document.fileId] });
      return url;
    } else {
      return "";
    }
  };

  const handleDocumentViewer = (path: string, name: string, documentRefId: string) => {
    setFileInfo({ filePath: path, fileName: name, documentId: documentRefId });
  };

  const handlePreviewDocument = async (doc: RetrieveDocumentsResponseDto) => {
    // Skip preview if document is restricted (isNoPreview)
    if (doc.isNoPreview) {
      return;
    }

    setFileName(doc.name);
    setShow(true);
    setSelectedDocument(doc);
    if (documentsByNaf) {
      const nafDoc = documentsByNaf.find((d) => d.palInstanceId === doc.fileId);
      if (nafDoc) {
        const url = getPathUrl(doc, nafDoc.documentContent?.contentType);
        if (url) handleDocumentViewer(url, doc.name, doc.fileId);
        return;
      }
    }
    const loaded = await getBlob(doc);
    if (loaded) {
      handleDocumentViewer(loaded.url, loaded.fileName, doc.fileId);
    } else {
      setDocumentLoadError(true);
    }
  };

  const handleDownload = async (doc: RetrieveDocumentsResponseDto | null) => {
    if (!doc) return;
    // Skip download if document is restricted (isNoPreview)
    if (doc.isNoPreview) return;

    let fileUrl = fileInfo.filePath;
    let downloadName = doc.isWireFraudDocument ? fileInfo.fileName : doc.name;
    if (!fileUrl) {
      if (documentsByNaf) {
        const nafDoc = documentsByNaf.find((d) => d.palInstanceId === doc.fileId);
        if (nafDoc) {
          fileUrl = getPathUrl(doc, nafDoc.documentContent?.contentType);
        } else {
          const loaded = await getBlob(doc);
          fileUrl = loaded?.url ?? "";
          if (loaded?.fileName) {
            downloadName = loaded.fileName;
          }
        }
      }

      if (!fileUrl) return;
      setSelectedDocument((prev) => (prev?.fileId === doc.fileId ? { ...prev, url: fileUrl } : prev));
    }

    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = downloadName || doc.name;
    link.target = "_blank";
    link.rel = "noreferrer";
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const [documents, setDocuments] = useState<RetrieveDocumentsResponseDto[]>([]);
  useEffect(() => {
    setDocuments(transformNeeds(needsByUser));
  }, [needsByUser]);

  useEffect(() => {
    if (pendingDocumentId && documentsByNaf.length > 0) {
      const nafDocuments = transformDocuments(documentsByNaf);
      const targetDocument = nafDocuments.find((doc) => doc.fileId === pendingDocumentId);
      // Only auto-preview if document is accessible (not restricted with isNoPreview)
      if (targetDocument && !targetDocument.isNoPreview) {
        void handlePreviewDocument(targetDocument);
        onDocumentOpened?.();
      }
    }
  }, [pendingDocumentId, documentsByNaf, handlePreviewDocument, onDocumentOpened, isRetreivePalLoading, isRetreivePalSuccess]);

  useEffect(() => {
    if (!isRetrieveNeedsLoading && !isRetreivePalLoading) {
      onLoad?.();
    }
  }, [isRetrieveNeedsLoading, isRetreivePalLoading, onLoad]);

  const dropdownOptions: DropdownOption[] = [
    {
      title: SORT_BY_DATE,
      isSelected: sortField === SORT_BY_DATE,
    },
    {
      title: SORT_BY_TITLE,
      isSelected: sortField === SORT_BY_TITLE,
    },
  ];

  const handleSort = (option: DropdownOption) => {
    if (sortField !== option.title) {
      setSortField(option.title);
      setSortOrder("asc");
    }
  };

  const wireFraudDocuments = useMemo(
    () => (dealApplicationDocuments ?? []).filter((doc) => doc.isWireFraudDocument),
    [dealApplicationDocuments],
  );

  const filteredDocuments = useMemo(() => {
    // Build base set according to selected category filter
    let base: RetrieveDocumentsResponseDto[] = [];
    if (filter === FILTER_NAF) {
      base = [...(transformDocuments(documentsByNaf) || []), ...wireFraudDocuments];
    } else if (filter === "user") {
      base = documents || [];
    } else if (filter === "all") {
      base = (documents || []).concat(transformDocuments(documentsByNaf) || [], wireFraudDocuments);
    }

    // Apply search filtering (case-insensitive) within the selected category
    const query = searchQuery?.trim().toLowerCase() ?? "";
    let filtered = base;
    if (query.length > 0) {
      filtered = base?.filter((doc) => {
        const name = (doc.name || "").toLowerCase();
        return name.includes(query);
      });
    }

    // Apply sorting to the filtered results
    filtered.sort((a, b) => {
      let comparison = 0;

      if (sortField === SORT_BY_DATE) {
        comparison = getDocumentSortTime(a) - getDocumentSortTime(b);
      } else if (sortField === SORT_BY_TITLE) {
        comparison = (a.name || "").localeCompare(b.name || "");
      }

      return sortOrder === "asc" ? comparison : -comparison;
    });

    setDisableSort(filtered.length == 0);
    setDisableUploadBtn(filter === FILTER_NAF || (!dealApplicationId && !featureFlag?.encompassApplicationDocumentUploadEnabled));
    return filtered;
  }, [filter, sortField, sortOrder, documents, documentsByNaf, searchQuery, wireFraudDocuments]);

  const renderModal = () => {
    const isLoading = isDocumentLoading || isDocumentFetching;
    const hasFilePath = Boolean(fileInfo.filePath);

    return (
      <Drawer
        isOpen={show}
        onClose={() => setShow(false)}
        title={fileName}
        customCss={"preview-document"}
        handleDownload={() => handleDownload(selectedDocument)}
        hasError={isDocumentError}
        type={DrawerType.DocumentPreview}
        centerContent={isLoading}
        closeText={selectedDocument?.isWireFraudDocument ? "Close" : undefined}
      >
        {isLoading && (
          <>
            <Spinner isApi />
            <span className={styles.spinnerText}>Loading your document This will just take a moment</span>
          </>
        )}
        {!isLoading && hasFilePath && (
          <DocumentPreview
            fileUrl={fileInfo.filePath}
            fileName={fileInfo.fileName}
            handleDownload={() => handleDownload(selectedDocument)}
            invalidReason={selectedDocument?.invalidReason}
            invalidDescription={selectedDocument?.detailedExplanation}
          />
        )}
        {((!isLoading && !hasFilePath) || documentLoadError) && (
          <div className={styles.previewError}>
            <img src={docErrorImg} alt="Error loading documents" className={styles.errorImage} />
            <p className={styles.errorText}>
              Something went wrong and we couldn't display your documents. Please try again later. We apologize for the inconvenience!
            </p>
          </div>
        )}
      </Drawer>
    );
  };
  const openDocumentUploadDrawer = () => {
    if (disableUploadBtn || isRetrieveNeedsError || isRetrievePalError || isNonBorrowerOwner) return;
    setShowDrawer(true);
  };

  const refetchDocListWithDelay = () => {
    globalThis.setTimeout(() => {
      void refetchDocList();
    }, 800);
  };

  const renderDocumentUploadDrawer = () => {
    const need =
      retrieveNeedsData?.find((n) => n.isDefault && n.isCanBeUsedForDocumentUpload) ??
      (featureFlag?.encompassApplicationDocumentUploadEnabled ? retrieveNeedsData?.find((n) => n.isEncompassUpload && n.isCanBeUsedForDocumentUpload) : undefined);
    return (
      <Drawer
        isOpen={showDrawer}
        onClose={() => {
          if (shouldRefetchDocList) {
            refetchDocListWithDelay();
          }
          setShouldRefetchDocList(false);
          setShowDrawer(false);
        }}
        type={DrawerType.DocumentUpload}
        title={need?.title}
        subTitle="You can upload additional files at any time, up to 12 files per upload."
        customCss="drawer-document-upload"
      >
        <>
          {need?.id && (
            <FileUploadPanel
              needId={need.id}
              isEncompassUpload={featureFlag?.encompassApplicationDocumentUploadEnabled && need.isEncompassUpload}
              handleByAction={() => {
                refetchDocListWithDelay();
                setShowDrawer(false);
              }}
              onSubmitSuccess={(submittedFileCount) => {
                trackFSDocumentsUploaded({
                  username,
                  timestamp: new Date().toISOString(),
                  prospectId,
                  dealId: dealId || null,
                  losLoanNumber: loanNumber || null,
                  losId: losId || null,
                  numberOfDocumentsUploaded: submittedFileCount,
                  loanMilestone: loanProgress?.status ?? "Unknown",
                });
                trackFSGuidedExperienceFirstTaskCompletedIfEligible({
                  username,
                  prospectId,
                  dealId: dealId || null,
                  losLoanNumber: loanNumber || null,
                });
                setShouldRefetchDocList(submittedFileCount > 0);
              }}
            />
          )}
        </>
      </Drawer>
    );
  };

  const renderDocuments = () => {
    if (isRetrieveNeedsLoading || isDocListFetching) {
      return (
        <div className={styles.docLoadingContainer}>
          <Spinner isApi />
          <span className={styles.spinnerText}>Loading your document This will just take a moment</span>
        </div>
      );
    }

    if (isRetrieveNeedsError) {
      return (
        <div className={styles.errorContainer}>
          <img src={docErrorImg} alt="Error loading documents" className={styles.errorImage} />
          <p className={styles.errorText}>We are having issues loading your documents. Try again later.</p>
        </div>
      );
    } else if (filteredDocuments.length === 0 && searchQuery.trim().length === 0) {
      return (
        <div className={styles.noDocContainer}>
          <img src={noDocumentImg} alt="Click Upload to add a file" className={styles.errorImage} />
          <p className={styles.clickToAddDocumentText}>Click Upload to add a file</p>
        </div>
      );
    } else if (filteredDocuments.length === 0 && searchQuery.trim().length > 0) {
      return (
        <div className={styles.noDocContainer}>
          <img src={searchNotFoundImg} alt="No documents found" className={styles.searchNotFoundImg} />
          <p className={styles.noDocumentsFoundText}>No documents found</p>
        </div>
      );
    } else {
      return filteredDocuments.map((doc, index) => (
        <DocumentCard
          documentId={doc.fileId}
          key={`${doc.fileId} ${index}`}
          documentName={doc.name}
          documentInfo={doc.isWireFraudDocument ? (doc.acknowledgedOn ?? "") : doc.dateCreated}
          dateLabel={getDocumentDateLabel(doc)}
          isInvalidDocument={isDocumentMarkedInvalid(doc)}
          isNoPreview={Boolean(doc.isNoPreview)}
          handleDocumentViewer={() => handlePreviewDocument(doc)}
          handleDownLoad={() => handleDownload(doc)}
        />
      ));
    }
  };

  useEffect(() => {
    onLoad?.();
  }, []);

  return (
    <div className={styles.content}>
      <div className={styles.documents}>Documents</div>
      <div className={styles.viewSearchFor}>View, search for, and sort your application documents.</div>
      <SearchBar placeholder="Search documents" initialValue={searchQuery} onSearch={setSearchQuery} />
      <TogglePills options={documentFilters} value={filter} onChange={setFilter} />
      <div className={styles.btnActions}>
        <Dropdown
          options={dropdownOptions}
          disable={disableSort}
          onSelection={handleSort}
          onToggleOrder={() => setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))}
        />
        <div
          className={`${styles.uploadWrapper} ${disableUploadBtn || isRetrieveNeedsError || isRetrievePalError || isNonBorrowerOwner ? styles.uploadWrapperDisabled : ""}`}
        >
          <button type="button" className={styles.upload} onClick={openDocumentUploadDrawer}>
            <div className={styles.uploadIcon}>
              <img className={styles.uploadPrimaryIcon} alt="upload" src={upload} />
            </div>
            <div className={styles.uploadPhrase}>Upload</div>
          </button>
        </div>
      </div>
      <div className={styles.documentCard}>{renderDocuments()}</div>
      {show && renderModal()}
      {showDrawer && renderDocumentUploadDrawer()}
    </div>
  );
};

export default Documents;
